import type { ColumnSort } from '@tanstack/react-table';
import { useCallback, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Dropdown } from '~/components/Dropdown/Dropdown';
import { Pagination } from '~/components/Pagination';
import { cn } from '~/lib/cn';
import { isEnvEnabled } from '~/lib/env';
import { useArtistTracksQuery } from '~/modules/artist-roster/hooks/useArtistTracksQuery';
import { useReleasesUrlParams } from '~/modules/artist-roster/hooks/useReleasesUrlParams';
import type { TrackTableViewType } from '~/modules/artist-roster/hooks/useTrackTableViewUrlParam';
import {
  TRACK_TABLE_VIEW_OPTIONS,
  useTrackTableViewUrlParam,
} from '~/modules/artist-roster/hooks/useTrackTableViewUrlParam';
import type { ArtistDetails } from '~/modules/artist-roster/lib/types';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { DEFAULT_PAGE_SIZE } from '../constants';

interface TrackListHeaderProps {
  uaid: number | null;
  artistData?: ArtistDetails;
  columnSort?: ColumnSort;
  scrollUpCallback?: () => void;
}

export function TrackListHeader({
  uaid,
  artistData,
  columnSort,
  scrollUpCallback,
}: TrackListHeaderProps) {
  const { pageNumber, setPageNumber, filters } = useReleasesUrlParams();
  const { trackEvent } = useAnalytics('Artist Roster');
  const { t } = useTranslation('roster');
  const { releaseType, searchText } = filters;
  const { trackTableView, setTrackTableView } = useTrackTableViewUrlParam();

  const isTrackPreReleasesEnabled = isEnvEnabled(
    'VITE_WMG_TRACK_PRE_RELEASES_ENABLED',
  );

  const isSetBenchmarksEnabled = isEnvEnabled(
    'VITE_WMG_TRACK_SET_BENCHMARKS_ENABLED',
  );

  const isArtistCollaboratorTracksEnabled = isEnvEnabled(
    'VITE_WMG_ARTIST_COLLABORATOR_TRACKS_ENABLED',
  );

  const {
    isLoading: isLoadingTracks,
    data: tracksData,
    error,
  } = useArtistTracksQuery({
    uaid,
    columnSort,
    releaseType,
    searchText,
    pageNumber: 1,
    limit: 1,
    isReleased: isTrackPreReleasesEnabled ? true : undefined,
  });

  const totalTracks = tracksData?.pagination.total ?? 0;

  const handleSetPageNumber = useCallback(
    (pageNum: number) => {
      const direction = pageNum > pageNumber ? 'next_page' : 'previous_page';

      trackEvent('artistTrackTablePaginationClick', {
        artist_id: artistData?.partyId ?? '',
        artist_name: artistData?.name ?? '',
        direction,
      });

      setPageNumber(pageNum);
    },
    [
      artistData?.partyId,
      artistData?.name,
      pageNumber,
      setPageNumber,
      trackEvent,
    ],
  );

  const handleTableViewChange = useCallback(
    (selected: string) => {
      setTrackTableView(selected as TrackTableViewType);
    },
    [setTrackTableView],
  );

  useEffect(() => {
    scrollUpCallback?.();
  }, [scrollUpCallback, pageNumber]);

  if (!artistData) {
    return null;
  }

  if (error) {
    return <div>{t('trackList.errors.loadingTracks')}</div>;
  }

  return (
    <div className="mb-md inline-flex min-h-xl w-full items-center justify-between">
      <div className="flex gap-xs">
        <Trans
          t={t}
          i18nKey="trackList.header.headingWithCount"
          values={{ count: totalTracks }}
          components={{
            h2: <h2 className="inline"> </h2>,
            span: (
              <span
                className={cn('text-subtle', { hidden: isLoadingTracks })}
              />
            ),
          }}
        />
      </div>
      <div className="flex items-center gap-lg">
        {isArtistCollaboratorTracksEnabled ? (
          <div className="flex items-center gap-sm">
            <div className="text-body-sm">
              {t('trackList.tableView.dropdown.label')}
            </div>
            <div className="w-[230px]">
              <Dropdown
                onChange={handleTableViewChange}
                contentClassName="z-20 text-body-sm"
                options={TRACK_TABLE_VIEW_OPTIONS.map((option) => ({
                  value: option,
                  label: t(`trackList.tableView.dropdown.options.${option}`),
                }))}
                value={trackTableView}
              />
            </div>
          </div>
        ) : null}
        <div className="flex gap-md text-caption-md">
          {isSetBenchmarksEnabled ? (
            <>
              <span>
                <Icon
                  className="pr-xs text-primary-default"
                  type="square"
                  size="xs"
                  variant="solid"
                />
                {t('benchmarks.adjusted')}
              </span>
              <span>
                <Icon
                  className="pr-xs text-tertiary-default"
                  type="square"
                  size="xs"
                  variant="solid"
                />
                {t('benchmarks.default')}
              </span>
            </>
          ) : null}
        </div>
        <Pagination
          page={pageNumber}
          total={totalTracks}
          perPage={DEFAULT_PAGE_SIZE}
          entity="tracks"
          setPageNumber={handleSetPageNumber}
          showAsLink={true}
        />
      </div>
    </div>
  );
}

TrackListHeader.displayName = 'TrackListHeader';
